<template>
    <Transition name="fade">
        <div v-if="shouldShowLoader" class="loading-overlay">
            <div class="loading-content">
                <div class="spinner"></div>
                <p v-if="loadingMessage" class="loading-message">{{ loadingMessage }}</p>
            </div>
        </div>
    </Transition>
</template>

<script>
import { defineComponent, computed, watch } from 'vue'
import { useLoadingManager } from '@/stores/loadingManager'
import { useGlobalStore } from '@/stores/global'
import { useRoute } from 'vue-router'

export default defineComponent({
    name: 'LoadingOverlay',
    setup() {
        const { isLoading, loadingMessage, forceStopLoading } = useLoadingManager()
        const globalStore = useGlobalStore()
        const route = useRoute()

        // Check if the loader should be shown based on global setting or route meta
        const shouldShowLoader = computed(() => {
            // If the route has forceLoader: true, always show the loader
            if (route.meta.forceLoader) {
                return isLoading.value
            }

            // Otherwise, respect the global setting
            return isLoading.value && globalStore.loaderEnabled
        })

        // Watch for route changes to handle transitions between routes with and without forceLoader
        watch(() => route.meta.forceLoader, (newValue, oldValue) => {
            // If navigating from a route with forceLoader to a route without forceLoader
            // and the global loader is disabled, force stop the loader
            if (oldValue && !newValue && !globalStore.loaderEnabled) {
                forceStopLoading()
            }
        })

        return {
            isLoading,
            loadingMessage,
            globalStore,
            shouldShowLoader
        }
    }
})
</script>

<style lang="scss" scoped>
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, .99);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.loading-content {
    text-align: center;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
}

.loading-message {
    color: #333;
    font-size: 1.1rem;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>